import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, extractAxiosError, fetchAdminUsersList } from "@/lib/api";
import { DeleteConfirmModal } from "@/components/DeleteConfirmModal";
import { PasswordInput } from "@/components/PasswordInput";
import { AdminPanelShell } from "@/components/admin/AdminPanelShell";
import { AdminUserProfileDialog } from "@/components/admin/AdminEntityProfileDialog";
import { Dialog, DialogFooter } from "@/components/EditPercentagesDialog";
import { KeyRound, Shield, Trash2, UserPlus, UserRoundSearch, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["user", "admin", "superadmin"];
const PLANS = ["free", "pro", "business"];

const ROLE_BADGE = {
    superadmin: "admin-role-badge admin-role-badge--superadmin",
    admin: "bg-amber-400/10 text-amber-300 border-amber-400/20",
    user: "bg-white/[0.06] text-white/55 border-white/10",
};

const PLAN_BADGE = {
    free: "bg-white/[0.06] text-white/55 border-white/10",
    pro: "bg-[#00BF0D]/10 text-[#00BF0D] border-[#00BF0D]/25",
    business: "bg-[#5D00FF]/15 text-[#B794FF] border-[#5D00FF]/30",
};

function UserProfileCell({ user, onOpen }) {
    return (
        <button
            type="button"
            onClick={() => onOpen(user.id)}
            className="group inline-flex items-center gap-1 rounded-md border border-white/[0.08] bg-white/[0.02] px-2.5 py-1.5 text-[11px] font-medium text-[#00BF0D]/90 transition-colors hover:border-[#00BF0D]/25 hover:bg-[#00BF0D]/[0.04] hover:text-[#00BF0D]"
            data-testid={`view-profile-${user.id}`}
        >
            <UserRoundSearch className="h-3 w-3 shrink-0" />
            View full profile
            <ChevronRight className="h-3 w-3 shrink-0 opacity-70" />
        </button>
    );
}

function UserRolePlanFields({ user, isSelf, roleClass, planClass, onRoleChange, onPlanChange }) {
    return (
        <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[88px]">
                {isSelf ? (
                    <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium border ${roleClass}`}>{user.role || "user"}</span>
                ) : (
                    <select
                        value={user.role || "user"}
                        onChange={(e) => onRoleChange(user.id, e.target.value)}
                        className="input-dark w-full rounded-md px-2 py-1 text-xs capitalize"
                        data-testid={`role-select-${user.id}`}
                    >
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                )}
            </div>
            <div className="min-w-[88px]">
                {isSelf ? (
                    <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium border capitalize ${planClass}`}>{user.plan || "free"}</span>
                ) : (
                    <select
                        value={user.plan || "free"}
                        onChange={(e) => onPlanChange(user.id, e.target.value)}
                        className="input-dark w-full rounded-md px-2 py-1 text-xs capitalize"
                        title="Sets subscription tier for this user and all orgs they own (pool limits, features, billing)"
                        data-testid={`plan-select-${user.id}`}
                    >
                        {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                )}
            </div>
        </div>
    );
}

function UserActions({ user, isSelf, onReset, onDelete }) {
    if (isSelf) {
        return (
            <span className="text-[10px] text-white/30 px-2 inline-flex items-center gap-1">
                <Shield className="h-3 w-3" /> you
            </span>
        );
    }

    return (
        <div className="flex items-center justify-end gap-1.5 flex-wrap">
            <button
                onClick={() => onReset(user)}
                className="btn-ghost rounded-md px-2 py-1 text-xs inline-flex items-center gap-1"
                title="Reset password"
                data-testid={`reset-password-${user.id}`}
            >
                <KeyRound className="h-3 w-3" /> Reset pwd
            </button>
            <button
                onClick={() => onDelete(user)}
                className="rounded-md px-2 py-1 text-xs inline-flex items-center gap-1 text-rose-400 hover:bg-rose-500/10 transition-colors"
                title="Delete user"
                data-testid={`delete-user-${user.id}`}
            >
                <Trash2 className="h-3 w-3" /> Delete
            </button>
        </div>
    );
}

function CreateUserModal({ open, onClose, onCreated }) {
    const [form, setForm] = useState({ email: "", password: "", name: "", role: "user", plan: "free" });
    const [busy, setBusy] = useState(false);

    const handleClose = () => {
        setForm({ email: "", password: "", name: "", role: "user", plan: "free" });
        onClose();
    };

    const submit = async () => {
        if (!form.email.trim() || !form.password.trim() || !form.name.trim()) {
            toast.error("Email, password, and name are required");
            return;
        }
        setBusy(true);
        try {
            await api.post("/admin/users", form);
            toast.success("User created");
            handleClose();
            onCreated();
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to create user"));
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <Dialog onClose={handleClose} title="Create user" subtitle="App users sign in at /login. Superadmin accounts must use /admin/login." testid="create-user-dialog">
            <div className="space-y-3">
                <div>
                    <label className="label-eyebrow block mb-1.5">Name</label>
                    <input className="input-dark w-full rounded-md px-3 py-2 text-sm" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                </div>
                <div>
                    <label className="label-eyebrow block mb-1.5">Email</label>
                    <input type="email" className="input-dark w-full rounded-md px-3 py-2 text-sm" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                </div>
                <div>
                    <label className="label-eyebrow block mb-1.5">Password</label>
                    <PasswordInput
                        value={form.password}
                        onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                        testId="create-user-password"
                        autoComplete="new-password"
                        placeholder="Temporary password"
                    />
                </div>
                <div>
                    <label className="label-eyebrow block mb-1.5">Platform role</label>
                    <select className="input-dark w-full rounded-md px-3 py-2 text-sm" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>
                <div>
                    <label className="label-eyebrow block mb-1.5">Subscription plan</label>
                    <select className="input-dark w-full rounded-md px-3 py-2 text-sm capitalize" value={form.plan} onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))} data-testid="create-user-plan">
                        {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
            </div>
            <DialogFooter>
                <button onClick={handleClose} className="btn-ghost rounded-md px-4 py-2 text-sm">Cancel</button>
                <button onClick={submit} disabled={busy} className="btn-primary rounded-md px-4 py-2 text-sm">{busy ? "Creating…" : "Create user"}</button>
            </DialogFooter>
        </Dialog>
    );
}

function ResetPasswordModal({ user, open, onClose }) {
    const [busy, setBusy] = useState(false);
    const [tempPassword, setTempPassword] = useState("");

    useEffect(() => {
        if (!open) setTempPassword("");
    }, [open]);

    const submit = async () => {
        if (!user?.id) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/users/${user.id}/reset-password`, {});
            setTempPassword(data.temporary_password);
            toast.success("Password reset");
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to reset password"));
        } finally {
            setBusy(false);
        }
    };

    if (!open || !user) return null;

    return (
        <Dialog onClose={onClose} title={`Reset password — ${user?.email || "user"}`} subtitle="Generates a temporary password. Share it securely with the user." testid="reset-password-dialog">
            {tempPassword ? (
                <div className="space-y-3">
                    <p className="text-sm text-white/55">Temporary password (shown once):</p>
                    <code className="admin-accent-code block rounded-md border border-white/10 bg-black/30 px-3 py-2 font-mono text-sm">{tempPassword}</code>
                </div>
            ) : (
                <p className="text-sm text-white/55">This will invalidate the user&apos;s current password and require them to sign in with a new temporary password.</p>
            )}
            <DialogFooter>
                <button onClick={onClose} className="btn-ghost rounded-md px-4 py-2 text-sm">{tempPassword ? "Close" : "Cancel"}</button>
                {!tempPassword && (
                    <button onClick={submit} disabled={busy} className="btn-primary rounded-md px-4 py-2 text-sm inline-flex items-center gap-1.5">
                        <KeyRound className="h-3.5 w-3.5" /> {busy ? "Resetting…" : "Generate temp password"}
                    </button>
                )}
            </DialogFooter>
        </Dialog>
    );
}

export function AdminUsersPage() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [profileUserId, setProfileUserId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteImpact, setDeleteImpact] = useState(null);
    const [impactLoading, setImpactLoading] = useState(false);
    const [resetTarget, setResetTarget] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await fetchAdminUsersList();
            setUsers(data.users || []);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load users"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const updateRole = async (userId, role) => {
        try {
            await api.patch(`/admin/users/${userId}/role`, { role });
            toast.success(`Role updated to ${role}`);
            load();
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to update role"));
        }
    };

    const updatePlan = async (userId, plan) => {
        try {
            await api.patch(`/admin/users/${userId}`, { plan });
            toast.success(`Plan updated to ${plan}`);
            load();
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to update plan"));
        }
    };

    const openDelete = async (user) => {
        setDeleteTarget(user);
        setDeleteImpact(null);
        setImpactLoading(true);
        try {
            const { data } = await api.get(`/admin/users/${user.id}/deletion-impact`);
            setDeleteImpact(data);
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to load deletion impact"));
        } finally {
            setImpactLoading(false);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setBusy(true);
        try {
            const { data } = await api.delete(`/admin/users/${deleteTarget.id}`);
            const cascade = data.cascade || {};
            toast.success(`User deleted (${cascade.orgs_deleted || 0} orgs, ${cascade.pools_deleted || 0} pools cleaned)`);
            setDeleteTarget(null);
            load();
        } catch (e) {
            toast.error(extractAxiosError(e, "Failed to delete user"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <AdminPanelShell
                title="Users"
                count={users.length}
                loading={loading}
                empty={!loading && users.length === 0}
                emptyMessage="No users found."
                variant="table"
                testId="admin-users-panel"
                headerExtra={(
                    <button onClick={() => setCreateOpen(true)} className="btn-primary rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1.5" data-testid="create-user-btn">
                        <UserPlus className="h-3.5 w-3.5" /> Create user
                    </button>
                )}
            >
                {/* Desktop table */}
                <div className="hidden md:block">
                    <table className="w-full min-w-[760px] text-sm">
                        <thead className="sticky top-0 bg-[#262626] z-10">
                            <tr className="border-b border-white/[0.06] text-left text-white/45 text-xs uppercase tracking-wider">
                                <th className="px-4 py-3 font-medium">Name</th>
                                <th className="px-4 py-3 font-medium">Email</th>
                                <th className="px-4 py-3 font-medium">Role</th>
                                <th className="px-4 py-3 font-medium" title="Subscription tier for this user and any orgs they own">Plan</th>
                                <th className="px-4 py-3 font-medium">Profile</th>
                                <th className="px-4 py-3 font-medium text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.04]">
                            {users.map((u) => {
                                const isSelf = u.id === currentUser?.id;
                                const roleClass = ROLE_BADGE[u.role] || ROLE_BADGE.user;
                                const planClass = PLAN_BADGE[u.plan] || PLAN_BADGE.free;
                                return (
                                    <tr key={u.id} className="hover:bg-white/[0.015] transition-colors align-top" data-testid={`admin-user-${u.id}`}>
                                        <td className="px-4 py-3 font-medium text-white">{u.name || "—"}</td>
                                        <td className="px-4 py-3 text-white/65 font-mono text-xs">{u.email}</td>
                                        <td className="px-4 py-3">
                                            {isSelf ? (
                                                <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium border ${roleClass}`}>{u.role || "user"}</span>
                                            ) : (
                                                <select
                                                    value={u.role || "user"}
                                                    onChange={(e) => updateRole(u.id, e.target.value)}
                                                    className="input-dark rounded-md px-2 py-1 text-xs capitalize"
                                                    data-testid={`role-select-${u.id}`}
                                                >
                                                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                                                </select>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {isSelf ? (
                                                <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium border capitalize ${planClass}`}>{u.plan || "free"}</span>
                                            ) : (
                                                <select
                                                    value={u.plan || "free"}
                                                    onChange={(e) => updatePlan(u.id, e.target.value)}
                                                    className="input-dark rounded-md px-2 py-1 text-xs capitalize"
                                                    title="Sets subscription tier for this user and all orgs they own (pool limits, features, billing)"
                                                    data-testid={`plan-select-${u.id}`}
                                                >
                                                    {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
                                                </select>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <UserProfileCell user={u} onOpen={setProfileUserId} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <UserActions user={u} isSelf={isSelf} onReset={setResetTarget} onDelete={openDelete} />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Mobile cards */}
                <div className="md:hidden divide-y divide-white/[0.04]">
                    {users.map((u) => {
                        const isSelf = u.id === currentUser?.id;
                        const roleClass = ROLE_BADGE[u.role] || ROLE_BADGE.user;
                        const planClass = PLAN_BADGE[u.plan] || PLAN_BADGE.free;
                        return (
                            <article key={u.id} className="space-y-3 p-4" data-testid={`admin-user-${u.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="font-medium text-white">{u.name || "—"}</p>
                                        <p className="truncate font-mono text-xs text-white/55">{u.email}</p>
                                    </div>
                                    {isSelf && (
                                        <span className="shrink-0 text-[10px] text-white/30 inline-flex items-center gap-1">
                                            <Shield className="h-3 w-3" /> you
                                        </span>
                                    )}
                                </div>

                                <UserProfileCell user={u} onOpen={setProfileUserId} />

                                <div>
                                    <p className="label-eyebrow mb-1.5">Role & plan</p>
                                    <UserRolePlanFields
                                        user={u}
                                        isSelf={isSelf}
                                        roleClass={roleClass}
                                        planClass={planClass}
                                        onRoleChange={updateRole}
                                        onPlanChange={updatePlan}
                                    />
                                </div>

                                {!isSelf && (
                                    <div className="flex flex-wrap gap-2 pt-1">
                                        <button
                                            onClick={() => setResetTarget(u)}
                                            className="btn-ghost rounded-md px-2.5 py-1.5 text-xs inline-flex items-center gap-1"
                                            data-testid={`reset-password-${u.id}`}
                                        >
                                            <KeyRound className="h-3 w-3" /> Reset pwd
                                        </button>
                                        <button
                                            onClick={() => openDelete(u)}
                                            className="rounded-md px-2.5 py-1.5 text-xs inline-flex items-center gap-1 text-rose-400 hover:bg-rose-500/10 transition-colors"
                                            data-testid={`delete-user-${u.id}`}
                                        >
                                            <Trash2 className="h-3 w-3" /> Delete
                                        </button>
                                    </div>
                                )}
                            </article>
                        );
                    })}
                </div>
            </AdminPanelShell>

            <CreateUserModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
            <ResetPasswordModal user={resetTarget} open={!!resetTarget} onClose={() => setResetTarget(null)} />
            <AdminUserProfileDialog userId={profileUserId} open={!!profileUserId} onClose={() => setProfileUserId(null)} />

            <DeleteConfirmModal
                open={!!deleteTarget}
                onClose={() => { setDeleteTarget(null); setDeleteImpact(null); }}
                onConfirm={confirmDelete}
                busy={busy}
                title={`Delete ${deleteTarget?.email}?`}
                description="Permanently removes this user account. Workspaces and pools they own are deleted; invited memberships are removed without deleting shared orgs or pools."
            >
                {impactLoading && <p className="text-xs text-white/45">Loading deletion impact…</p>}
                {deleteImpact && (
                    <div className="rounded-lg border border-rose-500/20 bg-rose-500/[0.05] p-3 space-y-3 text-xs text-white/60">
                        <p>
                            This will delete <span className="font-mono text-white/80">{deleteImpact.organization_count}</span> owned organization(s)
                            and <span className="font-mono text-white/80">{deleteImpact.pool_count}</span> owned pool(s). Other memberships are detached only.
                        </p>
                        {deleteImpact.paid_subscription_count > 0 && (
                            <p className="text-amber-200/90 border border-amber-500/25 rounded-md px-2.5 py-2 bg-amber-500/[0.08]">
                                <strong className="font-medium">{deleteImpact.paid_subscription_count}</strong> owned workspace(s) have an active Pro/Business Stripe subscription.
                                Deleting this user will <strong className="font-medium">cancel billing immediately</strong> and remove those workspaces.
                            </p>
                        )}
                        {deleteImpact.warnings?.map((w) => (
                            <p key={w} className="text-white/50">{w}</p>
                        ))}
                        {deleteImpact.organizations?.length > 0 && (
                            <div>
                                <p className="label-eyebrow mb-1.5 text-rose-300/80">Organizations</p>
                                <ul className="space-y-1">
                                    {deleteImpact.organizations.map((org) => (
                                        <li key={org.id} className="flex justify-between gap-2">
                                            <span className="truncate text-white/75">
                                                {org.name}
                                                {org.has_active_subscription && (
                                                    <span className="ml-1 text-amber-300/90">({org.plan} · Stripe)</span>
                                                )}
                                            </span>
                                            <span className="shrink-0 text-white/40">{org.created_at ? new Date(org.created_at).toLocaleDateString() : "—"} · {org.pool_count ?? 0} pools</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {deleteImpact.pools?.length > 0 && (
                            <div>
                                <p className="label-eyebrow mb-1.5 text-rose-300/80">Pools</p>
                                <ul className="space-y-1 max-h-32 overflow-auto">
                                    {deleteImpact.pools.map((pool) => (
                                        <li key={pool.id} className="flex justify-between gap-2">
                                            <span className="truncate text-white/75">{pool.name}</span>
                                            <span className="shrink-0 text-white/40">{pool.created_at ? new Date(pool.created_at).toLocaleDateString() : "—"}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </DeleteConfirmModal>
        </>
    );
}
